// ============================================================
// Preços e cálculo de frete — Fretz
//
// Foco operacional: pequenos fretes e pequenas mudanças com
// picapes, Kombis, Fiorinos, HRs, baús 3/4 e similares. NÃO é
// motoboy — esse nicho está fora do escopo da Fretz.
//
// Valores calibrados para Goiânia/GO em 2026, com base em:
//   • Mercado local de fretes pequenos e mudanças (cooperativas
//     e autônomos da região)
//   • Custo combustível (gasolina ~R$ 6,20/L; diesel ~R$ 6,30/L)
//   • Manutenção/depreciação picape e caminhão pequeno
//   • Margem para entregador parceiro + plataforma (≈ 20%)
//
// Fórmula:
//   total = max( (km × R$/km) + (min × R$/min) + paradas, mínimo )
//   se retorno: total *= 2
//   se noturno (22-5h): total *= 1.30
//   se fim de semana: total *= 1.15
//   se ajuda de carregamento: + adicional fixo
// ============================================================

export const SERVICES = {
  // Pequeno frete — eletro/móvel avulso/carga de loja
  // Veículo: picape (Strada/Saveiro/Montana), Kombi, Fiorino, Doblò
  frete_pequeno: {
    id: 'frete_pequeno',
    name: 'Fretz Pequeno',
    tag: 'Mais usado',
    description: 'Eletrodoméstico, móvel avulso, carga de loja. Coleta em até 60 min.',
    vehicleLabel: 'Picape · Kombi · Fiorino',
    minPrice: 59.90,
    perKm:    4.80,
    perMin:   0.55,
    coverageBRL: 1500,
    eta: 'Até 90 min',
    features: [
      'Coleta em até 60 min',
      'Volumes até ~1m³',
      'Rastreio ao vivo',
      'Cobertura R$ 1.500',
    ],
    popular: true,
    capacityHint: 'até ~500 kg / 1 m³',
  },

  // Mudança pequena — quitinete, quarto, escritório pequeno
  // Veículo: caminhão baú HR, 3/4, Iveco pequeno
  mudanca_pequena: {
    id: 'mudanca_pequena',
    name: 'Fretz Mudança',
    tag: 'Mudanças',
    description: 'Quitinete, quarto, ou escritório pequeno. Com ajuda de carregamento opcional.',
    vehicleLabel: 'Baú HR · 3/4 · Iveco',
    minPrice: 199.00,
    perKm:    7.50,
    perMin:   1.20,
    coverageBRL: 5000,
    eta: 'Agendamento mesmo dia',
    features: [
      'Caminhão baú HR ou 3/4',
      'Volumes até ~12 m³',
      'Ajuda de carregamento opcional',
      'Cobertura R$ 5.000',
    ],
    capacityHint: 'até ~12 m³ / mudança quitinete-quarto',
  },

  // Frete agendado — mesma capacidade do "pequeno", mas com janela de
  // tempo planejada e desconto por previsibilidade
  frete_agendado: {
    id: 'frete_agendado',
    name: 'Fretz Agendado',
    tag: 'Mais barato',
    description: 'Marque dia e janela de horário. Mesmo veículo, preço menor.',
    vehicleLabel: 'Picape · Kombi · Fiorino',
    minPrice: 49.90,
    perKm:    3.90,
    perMin:   0.40,
    coverageBRL: 1500,
    eta: 'Janela de 2h',
    features: [
      'Você escolhe data e janela',
      'Volumes até ~1m³',
      'Tarifa reduzida',
      'Rastreio ao vivo',
    ],
    capacityHint: 'até ~500 kg / 1 m³',
    requiresScheduling: true,
  },
};

export const SURCHARGE = {
  return:    { rate: 2.00, label: 'Retorno' },
  night:     { rate: 1.30, label: 'Noturno (22h–5h)' },
  weekend:   { rate: 1.15, label: 'Fim de semana' },
  // Ajuda de carregamento — adicional fixo, depende do serviço:
  // R$ 60 para frete pequeno, R$ 120 para mudança
  helperFreteFlat:   60.00,
  helperMudancaFlat: 120.00,
};

export const SERVICE_LIST = Object.values(SERVICES);

// Subset usado no simulador da home
export const SIMULATOR_SERVICES = [
  SERVICES.frete_pequeno,
  SERVICES.mudanca_pequena,
  SERVICES.frete_agendado,
];

export function isNightHour(date = new Date()) {
  const h = date.getHours();
  return h >= 22 || h < 5;
}

export function isWeekend(date = new Date()) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

/**
 * Calcula o preço final de um frete.
 *
 * @param {Object} opts
 * @param {string} opts.serviceId       'frete_pequeno' | 'mudanca_pequena' | 'frete_agendado'
 * @param {number} opts.distanceKm      Distância em km
 * @param {number} opts.durationMin     Duração estimada em minutos
 * @param {boolean} opts.isReturnTrip   Aplica multiplicador de retorno
 * @param {boolean} opts.applyNight     Aplica acréscimo noturno
 * @param {boolean} opts.applyWeekend   Aplica acréscimo fim de semana
 * @param {boolean} opts.withHelper     Inclui ajuda de carregamento
 * @returns {{ total: number, breakdown: object }} Preço final e quebra de cálculo
 */
export function calculateFare({
  serviceId    = 'frete_pequeno',
  distanceKm   = 0,
  durationMin  = 0,
  isReturnTrip = false,
  applyNight   = false,
  applyWeekend = false,
  withHelper   = false,
} = {}) {
  const s = SERVICES[serviceId] ?? SERVICES.frete_pequeno;

  const distanceCost = distanceKm  * s.perKm;
  const timeCost     = durationMin * s.perMin;
  const baseRaw      = distanceCost + timeCost;
  const oneWay       = Math.max(baseRaw, s.minPrice);

  let total = isReturnTrip ? oneWay * SURCHARGE.return.rate : oneWay;
  if (applyNight)   total *= SURCHARGE.night.rate;
  if (applyWeekend) total *= SURCHARGE.weekend.rate;

  let helperCost = 0;
  if (withHelper) {
    helperCost = s.id === 'mudanca_pequena'
      ? SURCHARGE.helperMudancaFlat
      : SURCHARGE.helperFreteFlat;
    total += helperCost;
  }

  total = Math.round(total * 100) / 100;

  return {
    total,
    breakdown: {
      service: s.name,
      distanceCost: round2(distanceCost),
      timeCost:     round2(timeCost),
      helperCost:   round2(helperCost),
      minimumApplied: baseRaw < s.minPrice,
      minimumValue:   s.minPrice,
      returnApplied:  isReturnTrip,
      nightApplied:   applyNight,
      weekendApplied: applyWeekend,
    },
  };
}

const round2 = (n) => Math.round(n * 100) / 100;

export const formatBRL = (value) =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
